// Billing client: API hooks + Pakasir hosted checkout / Paddle.js loader. Server counterpart: src/lib/payments.server.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qk, type PricingPlanDto } from '#/lib/api'

import type { AddonKind, AddonPrice, Currency } from '#/lib/addons'
export type { AddonKind, Currency } from '#/lib/addons'
export type Provider = 'pakasir' | 'paddle'
export type TxStatus = 'pending' | 'paid' | 'deny' | 'cancel' | 'expire' | 'refund' | 'failed' | string

export interface VoucherDto {
  id?: string
  code: string
  kind: 'percent' | 'fixed' | string
  value: number
  appliesTo: string[] | null
  isAuto?: boolean
  endsAt?: string | null
}
export interface VoucherPricing {
  planSlug: string
  currency: Currency
  price: number
  discount: number
  total: number
}
export interface CreateTransactionResult {
  transactionId: string
  orderId: string
  provider: Provider
  currency: Currency
  amount: number
  price: number
  discount: number
  voucher: VoucherDto | null
  kind: 'plan' | 'addon'
  addon: AddonKind | null
  planSlug: string | null
  status: TxStatus
  configured: boolean
  activated?: boolean
  /** dev only: gateway missing but DEV_LOGIN=1 */
  simulate?: boolean
  // pakasir — hosted checkout page (QRIS + virtual accounts)
  checkoutUrl?: string
  // paddle
  priceId?: string
  clientToken?: string
  env?: 'sandbox' | 'production'
  customData?: { orderId: string; userId: string }
}
export interface TransactionDto {
  id: string
  orderId: string
  planId: string | null
  grossAmount: number
  discountAmount: number
  status: TxStatus
  paymentType: string | null
  provider: Provider
  providerRef: string | null
  paidAt: string | null
  createdAt: string
  kind: 'plan' | 'addon' | 'review'
  addon: AddonKind | null
  planName: string | null
  planSlug: string | null
  currency: Currency
  /** Pakasir: hosted page to (re)open while the order is pending. */
  checkoutUrl: string | null
}
export interface VerifyResult {
  transaction: TransactionDto
  status: TxStatus
  activated: boolean
}
export interface PaymentsConfig {
  pakasir: { configured: boolean; sandbox: boolean; qrisOnly: boolean; methods: string[] }
  paddle: { configured: boolean; clientToken: string | null; env: 'sandbox' | 'production'; scriptUrl: string }
  addons: Record<AddonKind, Record<Currency, AddonPrice>>
  dev: boolean
  /** viewer is an admin — sandbox settlement is gated on this */
  admin: boolean
}
export interface SubscriptionHistoryItem {
  id: string
  planName: string
  planSlug: string
  currency: Currency
  status: string
  startsAt: string
  expiresAt: string
  state: 'scheduled' | 'active' | 'cancelled' | 'expired'
}
export type CancelReason = 'too_expensive' | 'missing_features' | 'rarely_used' | 'found_alternative' | 'just_trying' | 'other'
export const CANCEL_REASONS: CancelReason[] = ['too_expensive', 'missing_features', 'rarely_used', 'found_alternative', 'just_trying', 'other']

export const qkb = {
  config: ['payments', 'config'] as const,
  transactions: ['transactions'] as const,
  history: ['subscription', 'history'] as const,
  autoVoucher: (slug?: string) => ['vouchers', 'auto', slug ?? ''] as const,
}

export const usePaymentsConfig = () => useQuery({ queryKey: qkb.config, queryFn: () => api<PaymentsConfig>('/api/payments/config'), staleTime: 300_000 })
export const useTransactions = (enabled = true) => useQuery({ queryKey: qkb.transactions, queryFn: () => api<{ transactions: TransactionDto[] }>('/api/payments/transactions'), enabled })
export const useSubscriptionHistory = (enabled = true) => useQuery({ queryKey: qkb.history, queryFn: () => api<{ subscriptions: SubscriptionHistoryItem[] }>('/api/subscription/history'), enabled })
export const useAutoVoucher = (planSlug?: string) => useQuery({ queryKey: qkb.autoVoucher(planSlug), queryFn: () => api<{ voucher: VoucherDto | null }>(`/api/vouchers/auto${planSlug ? `?planSlug=${encodeURIComponent(planSlug)}` : ''}`), staleTime: 60_000 })

export const applyVoucher = (code: string, planSlug?: string) => api<{ voucher: VoucherDto; pricing: VoucherPricing | null }>('/api/vouchers/apply', { method: 'POST', json: { code, planSlug } })
export const createTransaction = (body: { planSlug?: string; addon?: AddonKind; currency?: Currency; voucherCode?: string }) => api<CreateTransactionResult>('/api/payments/create-transaction', { method: 'POST', json: body })
export const verifyTransaction = (body: { orderId: string; providerRef?: string; simulate?: 'settlement' | 'pending' | 'deny' | 'cancel' | 'expire' }) => api<VerifyResult>('/api/payments/verify', { method: 'POST', json: body })
export const cancelTransaction = (orderId: string) => api<{ transaction: TransactionDto; status: TxStatus }>('/api/payments/cancel-transaction', { method: 'POST', json: { orderId } })

/** Invalidate everything a payment/cancel can change. */
export function useInvalidateBilling() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: qk.subscription })
    qc.invalidateQueries({ queryKey: qkb.transactions })
    qc.invalidateQueries({ queryKey: qkb.history })
  }
}

export function useCancelSubscription() {
  const invalidate = useInvalidateBilling()
  return useMutation({
    mutationFn: (body: { reason: CancelReason; note?: string }) => api<{ ok: true; expiresAt: string }>('/api/subscription/cancel', { method: 'POST', json: body }),
    onSuccess: invalidate,
  })
}

/** Client-side mirror of the server discount rule (percent / fixed), for previews only. */
export function previewDiscount(price: number, v: Pick<VoucherDto, 'kind' | 'value'> | null) {
  if (!v || price <= 0) return 0
  if (v.kind === 'percent') return Math.min(price, Math.round((price * Math.min(100, Math.max(0, v.value))) / 100))
  if (v.kind === 'fixed') return Math.min(price, Math.max(0, v.value))
  return 0
}
export const basePlanSlug = (slug: string) => slug.replace(/-usd$/, '').replace(/-\d+mo$/, '')
export const voucherAppliesTo = (v: Pick<VoucherDto, 'appliesTo'>, plan: Pick<PricingPlanDto, 'slug'>) => !v.appliesTo?.length || v.appliesTo.includes(plan.slug) || v.appliesTo.includes(basePlanSlug(plan.slug))

// ---------- gateway loaders (browser only)

declare global {
  interface Window {
    Paddle?: {
      Environment: { set: (env: 'sandbox' | 'production') => void }
      Initialize: (opts: { token: string; eventCallback?: (ev: { name: string; data?: { transaction_id?: string } }) => void }) => void
      Checkout: { open: (opts: Record<string, unknown>) => void; close?: () => void }
    }
  }
}
/**
 * Pakasir checkout is a hosted page — send the buyer there. It brings them back to
 * `/transactions?order=<orderId>` (the `redirect` we passed), which re-verifies the order once.
 */
export function payWithPakasir(checkoutUrl: string) {
  window.location.assign(checkoutUrl)
}

const scriptPromises = new Map<string, Promise<void>>()
function loadScript(src: string, attrs: Record<string, string> = {}) {
  const existing = scriptPromises.get(src)
  if (existing) return existing
  const p = new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve()
    const s = document.createElement('script')
    s.src = src
    s.async = true
    for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v)
    s.onload = () => resolve()
    s.onerror = () => {
      scriptPromises.delete(src)
      reject(new Error(`Failed to load ${src}`))
    }
    document.head.appendChild(s)
  })
  scriptPromises.set(src, p)
  return p
}

let paddleReady: Promise<void> | null = null
/** Load Paddle.js once, initialise with the client token, then open the overlay checkout. */
export async function payWithPaddle(cfg: PaymentsConfig['paddle'], tx: CreateTransactionResult, opts: { email?: string | null; onCompleted: (transactionId?: string) => void; onClosed?: () => void }) {
  const token = tx.clientToken ?? cfg.clientToken
  if (!token || !tx.priceId) throw new Error('Paddle is not configured')
  if (!paddleReady) {
    paddleReady = loadScript(cfg.scriptUrl).then(() => {
      if (!window.Paddle) throw new Error('Paddle.js did not initialise')
      if ((tx.env ?? cfg.env) === 'sandbox') window.Paddle.Environment.set('sandbox')
      window.Paddle.Initialize({
        token,
        eventCallback: (ev) => {
          const h = paddleHandlers
          if (!h) return
          if (ev.name === 'checkout.completed') h.onCompleted(ev.data?.transaction_id)
          if (ev.name === 'checkout.closed') h.onClosed?.()
        },
      })
    })
  }
  await paddleReady
  paddleHandlers = opts
  window.Paddle!.Checkout.open({
    items: [{ priceId: tx.priceId, quantity: 1 }],
    customData: tx.customData ?? { orderId: tx.orderId },
    customer: opts.email ? { email: opts.email } : undefined,
    settings: { displayMode: 'overlay', variant: 'one-page' },
  })
}
let paddleHandlers: { onCompleted: (transactionId?: string) => void; onClosed?: () => void } | null = null

/** `GET /api/subscription/status` extras added by the billing layer (see quota.server.ts subscriptionStatus). */
export type SubscriptionStatusExt = import('#/lib/api').SubscriptionStatus & {
  cancelAtPeriodEnd: boolean
  subscription: (import('#/lib/api').SubscriptionStatus['subscription'] & { cancelAtPeriodEnd: boolean }) | null
  nextSubscription: { id: string; planId: string; status: string; startsAt: string; expiresAt: string; plan: PricingPlanDto } | null
}
